import { Button } from '../../ds';
import type { HealthTile } from '../../lib/contract';

interface Props {
  incidents: HealthTile[];
  onInvestigate: (h: HealthTile) => void;
  onDismiss: () => void;
}

/** Red-alert ribbon — surfaces the highest-severity active incident (the first
    red health tile). Color is never the only signal: it carries an icon + the
    word "Red alert" + the failing system's name. Dismissible. */
export function AlertRibbon({ incidents, onInvestigate, onDismiss }: Props) {
  const top = incidents.find((h) => h.status === 'red');
  if (!top) return null;

  // Craft the incident line the way the prototype does: a source file in mono/danger.
  const file = `${top.detail} ${top.metric}`.match(/[\w-]+\.(?:ts|tsx|js|jsx|astro|css|sql)\b/)?.[0];
  const cap = (s: string) => (s ? s[0].toUpperCase() + s.slice(1) : s);

  return (
    <div role="alert" style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '12px 14px', borderRadius: 'var(--radius-md)', background: 'var(--danger-soft)', border: '1px solid var(--danger-line)', boxShadow: '0 14px 40px -30px var(--danger)' }}>
      <span style={{ display: 'flex', color: 'var(--danger)', flex: 'none' }}>
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.2} strokeLinecap="round" strokeLinejoin="round">
          <path d="M12 9v4M12 17h.01M10.3 3.9 1.8 18a2 2 0 0 0 1.7 3h16.9a2 2 0 0 0 1.7-3L13.7 3.9a2 2 0 0 0-3.4 0Z" />
        </svg>
      </span>
      <span style={{ flex: 1, fontSize: 13.5, color: 'var(--ink)', lineHeight: 1.4 }}>
        <b style={{ fontWeight: 600 }}>Red alert · {top.name}.</b> {cap(top.sub)} spiked
        {file && (
          <> in <code style={{ fontFamily: 'var(--font-mono)', color: 'var(--danger)' }}>{file}</code></>
        )}
        . Deploy paused.
      </span>
      <Button variant="secondary" size="sm" onClick={() => onInvestigate(top)}>
        Investigate
      </Button>
      <button
        onClick={onDismiss}
        aria-label="Dismiss alert"
        style={{ flex: 'none', background: 'none', border: 'none', color: 'var(--muted)', cursor: 'pointer', padding: 4, lineHeight: 0, borderRadius: 6 }}
      >
        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round">
          <path d="M18 6 6 18M6 6l12 12" />
        </svg>
      </button>
    </div>
  );
}
